/**
 * Access token lives in memory only, never in localStorage, so an injected
 * script cannot lift a long-lived credential from storage. The refresh token is
 * an httpOnly cookie the page cannot read at all; a reload recovers the session
 * by calling /v1/auth/refresh.
 */

export const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/+$/, "") ?? "";

let accessToken: string | null = null;
const listeners = new Set<(token: string | null) => void>();

export function getAccessToken() {
  return accessToken;
}

export function setAccessToken(token: string | null) {
  accessToken = token;
  listeners.forEach((l) => l(token));
}

export function onAccessTokenChange(listener: (token: string | null) => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

let refreshing: Promise<boolean> | null = null;

/**
 * Single-flight refresh: ten requests failing with 401 at once trigger one
 * refresh, not ten. Running ten in parallel would rotate the token ten times and
 * the server would treat all but the first as token reuse and end the session.
 */
export function refreshSession(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const res = await fetch(`${API_BASE}/v1/auth/refresh`, { method: "POST", credentials: "include" });
      if (!res.ok) {
        setAccessToken(null);
        return false;
      }
      const body = (await res.json()) as { accessToken: string };
      setAccessToken(body.accessToken);
      return true;
    } catch {
      // Network failure: keep the current state; the caller surfaces the error.
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

async function sendWithToken(req: Request) {
  const token = getAccessToken();
  if (token) req.headers.set("Authorization", `Bearer ${token}`);
  return fetch(req);
}

async function errorCode(res: Response): Promise<string | undefined> {
  try {
    const body = (await res.clone().json()) as { error?: { code?: string } };
    return body.error?.code;
  } catch {
    return undefined;
  }
}

/**
 * fetch used by the API client. On 401 it refreshes once and replays the request.
 * On membership_inactive it also refreshes: the server drops the workspace from
 * the new token, and the app then routes the user to onboarding / workspace pick.
 */
export async function authFetch(input: Request): Promise<Response> {
  const replay = input.clone();
  const res = await sendWithToken(input);
  const path = new URL(input.url, window.location.origin).pathname;
  if (path.startsWith("/v1/auth/")) return res;

  const needsRefresh =
    res.status === 401 || (res.status === 403 && (await errorCode(res)) === "membership_inactive");
  if (!needsRefresh) return res;

  const ok = await refreshSession();
  return ok ? sendWithToken(replay) : res;
}
